import React from 'react';
import { ChevronLeft } from 'lucide-react';
import { SidebarStepRow } from './SidebarStepRow';
import type { ToolStep } from './types';

export interface ToolSidebarProps {
  toolName: string;
  /** Optional "Tool · 01 / 08" subtitle above the tool name. */
  toolNumber?: string;
  usesCredits?: boolean;
  steps: ToolStep[];
  currentStepKey: string;
  completedKeys: Set<string>;
  onStepChange: (key: string) => void;
  onBackToStudio?: () => void;
  sessionLabel?: string;
  sessionStatus?: string;
}

/** Beige left rail. Holds back-to-studio link, tool identity, vertical step
 *  list, the brand-character illustration, and an optional session footer. */
export const ToolSidebar: React.FC<ToolSidebarProps> = ({
  toolName,
  toolNumber,
  usesCredits,
  steps,
  currentStepKey,
  completedKeys,
  onStepChange,
  onBackToStudio,
  sessionLabel,
  sessionStatus,
}) => {
  const currentIndex = steps.findIndex((s) => s.key === currentStepKey);

  return (
    <aside
      className="flex flex-col gap-[18px] sticky self-start"
      style={{
        background: 'var(--hj-beige)',
        borderRight: '1px solid var(--hj-beige-line)',
        padding: '20px 16px 16px',
        color: 'var(--hj-beige-ink)',
        top: 0,
        maxHeight: 'calc(100vh - 68px)',
        overflowY: 'auto',
      }}
    >
      {onBackToStudio && (
        <button
          type="button"
          onClick={onBackToStudio}
          className="self-start inline-flex items-center gap-1.5 bg-transparent border-0 cursor-pointer px-1.5 py-1 rounded-hj-sm text-xs"
          style={{ color: 'var(--hj-beige-ink-2)', fontFamily: 'inherit' }}
        >
          <ChevronLeft size={12} strokeWidth={2} />
          Back to Studio
        </button>
      )}

      {/* Tool identity */}
      <div>
        {toolNumber && (
          <div className="mono-label" style={{ color: 'var(--hj-beige-ink-3)', marginBottom: 6 }}>
            {toolNumber}
          </div>
        )}
        <h1
          className="m-0 font-hj-display"
          style={{
            fontSize: 22,
            fontWeight: 600,
            lineHeight: 1.05,
            letterSpacing: '-0.01em',
            color: 'var(--hj-beige-ink)',
          }}
        >
          {toolName}
        </h1>
        {usesCredits && (
          <div
            className="inline-flex items-center gap-1.5 mt-2.5 px-2 py-0.5 rounded-full"
            style={{
              background: 'var(--hj-beige-deep)',
              border: '1px solid var(--hj-beige-line)',
              color: 'var(--hj-beige-ink-2)',
            }}
          >
            <span
              aria-hidden
              // On beige-deep; the yellow alias made this dot effectively invisible.
              style={{ width: 5, height: 5, borderRadius: '50%', background: 'var(--hj-beige-ink-2)' }}
            />
            <span className="mono-label" style={{ fontSize: 9, color: 'inherit' }}>
              Uses video tokens
            </span>
          </div>
        )}
      </div>

      {/* Pipeline step list */}
      <nav className="flex flex-col gap-0.5">
        <div
          className="mono-label"
          style={{ color: 'var(--hj-beige-ink-3)', marginBottom: 6, paddingLeft: 4 }}
        >
          Pipeline
        </div>
        {steps.map((s, i) => {
          const isCurrent = s.key === currentStepKey;
          const isDone = completedKeys.has(s.key);
          const unlocked =
            i === 0 ||
            isDone ||
            isCurrent ||
            i <= currentIndex ||
            (i > 0 && completedKeys.has(steps[i - 1].key));
          return (
            <SidebarStepRow
              key={s.key}
              step={s}
              index={i}
              isCurrent={isCurrent}
              isDone={isDone}
              unlocked={unlocked}
              onClick={() => unlocked && onStepChange(s.key)}
            />
          );
        })}
      </nav>

      <div className="flex-1 min-h-[12px]" />

      {/* Brand character illustration */}
      <div
        aria-hidden="true"
        className="relative flex items-end justify-center mb-1"
        style={{ height: 150 }}
      >
        <img
          src="/brand/splotch-1.png"
          alt=""
          className="absolute pointer-events-none"
          style={{
            left: '50%',
            top: '52%',
            transform: 'translate(-50%, -50%) rotate(-8deg)',
            width: 200,
            height: 'auto',
            opacity: 0.7,
          }}
        />
        <img
          src="/brand/char-phone.png"
          alt=""
          className="relative block w-auto"
          style={{ height: 140 }}
        />
      </div>

      {/* Session footer — "what am I working on" stamp */}
      {sessionLabel && (
        <div
          className="rounded-hj-sm"
          style={{
            padding: 12,
            background: 'var(--hj-beige-deep)',
            border: '1px solid var(--hj-beige-line)',
            fontSize: 11.5,
          }}
        >
          <div className="mono-label" style={{ color: 'var(--hj-beige-ink-3)', marginBottom: 4 }}>
            Session
          </div>
          <div style={{ fontWeight: 600, color: 'var(--hj-beige-ink)' }}>{sessionLabel}</div>
          {sessionStatus && (
            <div
              className="inline-flex items-center gap-1.5 mt-0.5"
              style={{ color: 'var(--hj-beige-ink-3)' }}
            >
              <span
                aria-hidden
                style={{
                  width: 5,
                  height: 5,
                  borderRadius: '50%',
                  background: 'var(--hj-green)',
                }}
              />
              {sessionStatus}
            </div>
          )}
        </div>
      )}
    </aside>
  );
};
